import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MerchantCategoryChips, labelsForCategoryCodes } from "../../components/MerchantCategoryPicker";
import { safeProductOrProfileImageSrc } from "../../utils/safeMerchantImageFile";
import {
  apiUrl,
  createMarketplaceMerchantPortalAdmin,
  getMarketplaceMerchantPortalAdmin,
  getMerchantPortalAdminToken,
  listMarketplaceMerchantsForPortalAdmin,
  listMerchantBusinessCategories,
  verifyMarketplaceMerchantPortalAdmin,
} from "../../services/api";
import { FilterBar, SearchField } from "../../components/orderKit";
import { friendlyError } from "../../utils/friendlyError";

const isVerified = (m) => Boolean(m.verifiedAt ?? m.verified_at);

function formatInstant(v) {
  if (v == null) return "-";
  try {
    const d = typeof v === "string" ? new Date(v) : new Date(Number(v));
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch {
    return String(v);
  }
}

function Detail({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: "var(--text-primary)", marginTop: 4, wordBreak: "break-word" }}>{children}</div>
    </div>
  );
}

export default function MarketplaceMerchantsAdmin() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  /** all | pending | verified */
  const [verifyFilter, setVerifyFilter] = useState("all");
  const [verifyingId, setVerifyingId] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [modalId, setModalId] = useState(null);
  const [modalMerchant, setModalMerchant] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");

  useEffect(() => {
    let cancelled = false;
    listMerchantBusinessCategories()
      .then((rows) => {
        if (!cancelled) setCategoryOptions(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setCategoryOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const verifyOptions = useMemo(() => {
    const verified = rows.filter(isVerified).length;
    return [
      { id: "all", label: "All", count: rows.length },
      { id: "pending", label: "Awaiting verification", count: rows.length - verified, dot: "#8a6420" },
      { id: "verified", label: "Verified", count: verified, dot: "#2f7a57" },
    ];
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((m) => {
      if (verifyFilter === "verified" && !isVerified(m)) return false;
      if (verifyFilter === "pending" && isVerified(m)) return false;
      if (!q) return true;
      const id = String(m.id ?? "");
      const name = String(m.name ?? "").toLowerCase();
      const email = String(m.contact_email ?? m.contactEmail ?? "").toLowerCase();
      return id.includes(q) || name.includes(q) || email.includes(q);
    });
  }, [rows, search, verifyFilter]);

  const load = useCallback(async () => {
    if (!getMerchantPortalAdminToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await listMarketplaceMerchantsForPortalAdmin();
      if (!Array.isArray(data)) {
        const msg =
          data && typeof data === "object" && (data.message || data.error)
            ? String(data.message || data.error)
            : "Unexpected response when loading merchants (expected a JSON array). Check the API / browser console.";
        throw new Error(msg);
      }
      setRows(data);
    } catch (e) {
      if (String(e?.message || "").includes("401") || String(e?.message || "").includes("403")) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(friendlyError(e, "Failed to load merchants"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError("");
    try {
      await createMarketplaceMerchantPortalAdmin({ name });
      setNewName("");
      await load();
    } catch (err) {
      setError(friendlyError(err, "Create failed"));
    } finally {
      setCreating(false);
    }
  }

  async function openReviewModal(merchantId) {
    if (!merchantId) return;
    setModalId(merchantId);
    setModalMerchant(null);
    setModalLoading(true);
    setModalError("");
    try {
      const m = await getMarketplaceMerchantPortalAdmin(merchantId);
      setModalMerchant(m);
    } catch (err) {
      setModalError(friendlyError(err, "Could not load merchant details."));
      setModalId(null);
    } finally {
      setModalLoading(false);
    }
  }

  function closeModal() {
    setModalId(null);
    setModalMerchant(null);
    setModalError("");
    setModalLoading(false);
  }

  async function verifyFromModal() {
    if (!modalMerchant?.id) return;
    const merchantId = modalMerchant.id;
    setVerifyingId(merchantId);
    setModalError("");
    setError("");
    try {
      await verifyMarketplaceMerchantPortalAdmin(merchantId);
      await load();
      closeModal();
    } catch (err) {
      setModalError(friendlyError(err, "Verify failed"));
    } finally {
      setVerifyingId(null);
    }
  }

  const modalImgRaw =
    modalMerchant?.profileImageUrl && String(modalMerchant.profileImageUrl).startsWith("/")
      ? apiUrl(modalMerchant.profileImageUrl)
      : modalMerchant?.profileImageUrl || null;
  const modalImg = modalImgRaw ? safeProductOrProfileImageSrc(modalImgRaw, null) : null;

  async function openCompliancePdf(path) {
    if (!path || typeof path !== "string") return;
    const href = path.startsWith("/") ? apiUrl(path) : path;
    const token = getMerchantPortalAdminToken();
    setModalError("");
    try {
      const res = await fetch(href, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) {
        setModalError("Could not open PDF (forbidden or missing file).");
        return;
      }
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      window.open(u, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(u), 120_000);
    } catch {
      setModalError("Could not open PDF.");
    }
  }

  const modalCategoryLabels = modalMerchant
    ? labelsForCategoryCodes(categoryOptions, modalMerchant.businessCategories || [])
    : [];

  return (
    <div className="grid">
      <div className="card">
        <h2 style={{ marginTop: 0 }}>ODOS marketplace merchants</h2>
        <p style={{ marginTop: 0, color: "var(--text-secondary)", maxWidth: 800 }}>
          These businesses sell on ODOS: they use the merchant portal, catalog, orders, and on-platform fulfillment. They
          are separate from <strong>external partners</strong> (own ecommerce; Partner API only) under{" "}
          <strong>Partner companies</strong> in the sidebar.
        </p>

        {error ? (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 14,
              background: "var(--error-bg)",
              border: "1px solid var(--error-border)",
              color: "var(--error-text)",
            }}
          >
            {error}
          </div>
        ) : null}

        <section style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: "1.05rem", marginBottom: 12 }}>Add marketplace merchant</h3>
          <form onSubmit={handleCreate} className="row wrap" style={{ gap: 10, alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 240px" }}>
              <label htmlFor="mm-name" style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
                Display name
              </label>
              <input
                id="mm-name"
                value={newName}
                onChange={(ev) => setNewName(ev.target.value)}
                placeholder="Riverdale Coffee Roasters"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 14,
                  border: "1px solid var(--border-color)",
                  background: "var(--primary-light)",
                  color: "var(--text-primary)",
                }}
              />
            </div>
            <button
              type="submit"
              disabled={creating || !newName.trim()}
              style={{
                padding: "10px 20px",
                borderRadius: 14,
                border: "none",
                background: creating ? "#a59eae" : "var(--accent-primary)",
                color: "#ffffff",
                cursor: creating || !newName.trim() ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
            >
              {creating ? "Creating…" : "Create merchant"}
            </button>
          </form>
          <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 8, marginBottom: 0 }}>
            Full registration (email, address, …) can still be done via public self-registration; this creates a minimal
            row for admin bootstrap.
          </p>
        </section>

        <section style={{ marginTop: 28 }}>
          <div className="row wrap" style={{ gap: 12, alignItems: "flex-end", marginBottom: 12 }}>
            <h3 style={{ fontSize: "1.05rem", margin: 0, flex: "1 1 200px" }}>All marketplace merchants</h3>
            <SearchField value={search} onChange={setSearch} placeholder="Name, email, or id" ariaLabel="Search merchants" />
          </div>
          {rows.length > 0 ? (
            <div className="list-toolbar">
              <FilterBar options={verifyOptions} value={verifyFilter} onChange={setVerifyFilter} ariaLabel="Verification" />
            </div>
          ) : null}
          {loading ? (
            <p style={{ color: "var(--text-secondary)" }}>Loading…</p>
          ) : rows.length === 0 ? (
            <p style={{ color: "var(--text-secondary)" }}>No marketplace merchants yet.</p>
          ) : filteredRows.length === 0 ? (
            <p style={{ color: "var(--text-secondary)" }}>No merchants match this search or filter.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border-color)", textAlign: "left", color: "var(--text-secondary)" }}>
                    <th style={{ padding: "8px 6px" }}>ID</th>
                    <th style={{ padding: "8px 6px" }}>Name</th>
                    <th style={{ padding: "8px 6px" }}>Contact email</th>
                    <th style={{ padding: "8px 6px" }}>Verified</th>
                    <th style={{ padding: "8px 6px" }}>Created</th>
                    <th style={{ padding: "8px 6px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((m) => (
                    <tr key={m.id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                      <td style={{ padding: "10px 6px", fontFamily: "ui-monospace, monospace" }}>{m.id}</td>
                      <td style={{ padding: "10px 6px" }}>{m.name}</td>
                      <td style={{ padding: "10px 6px", color: "var(--text-secondary)" }}>{m.contact_email ?? m.contactEmail ?? "-"}</td>
                      <td style={{ padding: "10px 6px", color: "var(--text-secondary)" }}>
                        {(m.verifiedAt ?? m.verified_at) ? (
                          <span style={{ color: "var(--accent-primary)", fontWeight: 600 }}>Yes</span>
                        ) : (
                          <span>No</span>
                        )}
                      </td>
                      <td style={{ padding: "10px 6px", color: "var(--text-secondary)" }}>
                        {formatInstant(m.created_at ?? m.createdAt)}
                      </td>
                      <td style={{ padding: "10px 6px" }}>
                        <button
                          type="button"
                          onClick={() => openReviewModal(m.id)}
                          style={{
                            padding: "6px 12px",
                            borderRadius: 14,
                            border: "1px solid var(--border-color)",
                            background: "var(--primary-light)",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {modalId != null ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="merchant-review-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(46, 33, 84, 0.14)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) closeModal();
          }}
          onKeyDown={(ev) => {
            if (ev.key === "Escape") closeModal();
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: 560,
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              margin: 0,
            }}
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 id="merchant-review-title" style={{ marginTop: 0 }}>
              Merchant details
            </h3>
            {modalLoading ? (
              <p style={{ color: "var(--text-secondary)" }}>Loading…</p>
            ) : modalMerchant ? (
              <>
                <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
                  Review registration before verifying. Unverified merchants cannot sign in to the portal. Password resets
                  are self-service: merchants use <strong>Forgot password</strong> on the merchant sign-in page (OTP by
                  SMS to their registered phone).
                </div>
                <Detail label="Merchant ID">{modalMerchant.id}</Detail>
                <Detail label="Business name">{modalMerchant.name}</Detail>
                <Detail label="Contact email">{modalMerchant.contactEmail ?? "-"}</Detail>
                <Detail label="TIN">{modalMerchant.tin ?? "-"}</Detail>
                <Detail label="Registration number">{modalMerchant.registrationNumber ?? "-"}</Detail>
                <Detail label="Account kind">{modalMerchant.accountKind ?? "-"}</Detail>
                <Detail label="Verified at">{formatInstant(modalMerchant.verifiedAt ?? modalMerchant.verified_at)}</Detail>
                <Detail label="Created">{formatInstant(modalMerchant.createdAt ?? modalMerchant.created_at)}</Detail>
                <Detail label="Store categories">
                  {modalCategoryLabels.length ? (
                    <MerchantCategoryChips labels={modalCategoryLabels} />
                  ) : (
                    modalMerchant.businessType || "-"
                  )}
                </Detail>
                <Detail label="Office address">{modalMerchant.officeAddress ?? "-"}</Detail>
                <Detail label="Office coordinates">
                  {modalMerchant.officeLatitude != null && modalMerchant.officeLongitude != null
                    ? `${modalMerchant.officeLatitude}, ${modalMerchant.officeLongitude}`
                    : "-"}
                </Detail>
                <Detail label="Contact person">{modalMerchant.contactPerson ?? "-"}</Detail>
                <Detail label="Contact phone">{modalMerchant.contactPhone ?? "-"}</Detail>
                <Detail label="Storefront photo">
                  {modalImg ? (
                    <img alt="Storefront" src={modalImg} style={{ maxHeight: 120, borderRadius: 14, marginTop: 6 }} />
                  ) : (
                    "-"
                  )}
                </Detail>
                <Detail label="Registration documents (PDF)">
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                    {modalMerchant.tinDocumentUrl ? (
                      <button
                        type="button"
                        onClick={() => openCompliancePdf(modalMerchant.tinDocumentUrl)}
                        style={{
                          alignSelf: "flex-start",
                          padding: 0,
                          border: "none",
                          background: "none",
                          color: "var(--accent-primary)",
                          cursor: "pointer",
                          textDecoration: "underline",
                          fontSize: 14,
                        }}
                      >
                        TIN certificate
                      </button>
                    ) : (
                      <span>TIN certificate -</span>
                    )}
                    {modalMerchant.businessRegistrationPdfUrl ? (
                      <button
                        type="button"
                        onClick={() => openCompliancePdf(modalMerchant.businessRegistrationPdfUrl)}
                        style={{
                          alignSelf: "flex-start",
                          padding: 0,
                          border: "none",
                          background: "none",
                          color: "var(--accent-primary)",
                          cursor: "pointer",
                          textDecoration: "underline",
                          fontSize: 14,
                        }}
                      >
                        Business registration
                      </button>
                    ) : (
                      <span>Business registration -</span>
                    )}
                    {modalMerchant.businessLicensePdfUrl ? (
                      <button
                        type="button"
                        onClick={() => openCompliancePdf(modalMerchant.businessLicensePdfUrl)}
                        style={{
                          alignSelf: "flex-start",
                          padding: 0,
                          border: "none",
                          background: "none",
                          color: "var(--accent-primary)",
                          cursor: "pointer",
                          textDecoration: "underline",
                          fontSize: 14,
                        }}
                      >
                        Business license
                      </button>
                    ) : (
                      <span>Business license -</span>
                    )}
                  </div>
                </Detail>

                {modalError ? (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      borderRadius: 14,
                      background: "var(--error-bg)",
                      border: "1px solid var(--error-border)",
                      color: "var(--error-text)",
                    }}
                  >
                    {modalError}
                  </div>
                ) : null}

                <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={closeModal}
                    style={{
                      padding: "10px 18px",
                      borderRadius: 14,
                      border: "1px solid var(--border-color)",
                      background: "var(--primary-light)",
                      color: "var(--text-primary)",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                  {!(modalMerchant.verifiedAt ?? modalMerchant.verified_at) ? (
                    <button
                      type="button"
                      disabled={verifyingId === modalMerchant.id}
                      onClick={verifyFromModal}
                      style={{
                        padding: "10px 18px",
                        borderRadius: 14,
                        border: "none",
                        background: verifyingId === modalMerchant.id ? "#a59eae" : "var(--accent-primary)",
                        color: "#ffffff",
                        fontWeight: 600,
                        cursor: verifyingId === modalMerchant.id ? "wait" : "pointer",
                      }}
                    >
                      {verifyingId === modalMerchant.id ? "Verifying…" : "Verify merchant"}
                    </button>
                  ) : null}
                </div>
              </>
            ) : (
              <p style={{ color: "var(--error-text)" }}>{modalError || "Unable to load merchant."}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
